import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import BaseBold from '@tiptap/extension-bold';
import BaseItalic from '@tiptap/extension-italic';
import Collaboration from '@tiptap/extension-collaboration';
import type { Story, Thread } from '@proofroom/schema';
import { resolveLeading } from '@proofroom/schema';
import type { SnapshotIndex } from '../model/snapshotIndex';
import { isAvailableFace } from '../model/fontLock';
import { storyToDoc } from '../model/storyToDoc';
import type { PMNode } from '../model/pmJson';
import { CharStyleMark, CommentMark, FaceMark, ParagraphIdentity, StyledParagraph, type EditorStyleInfo } from './extensions';
import { selectionInfo, type SelectionInfo } from './selection';
import type { Room } from './rooms';

export interface StoryEditorProps {
  story: Story;
  idx: SnapshotIndex;
  room: Room;
  threads: Thread[];
  activeThreadId?: string;
  editorRef: MutableRefObject<Editor | null>;
  onChange(doc: PMNode): void;
  onSelection(info: SelectionInfo): void;
  onThreadClick(threadId: string): void;
}

/*
 * The toolbar buttons refuse bold and italic when the draft has no face for them, so the
 * marks must have no other entry point: Mod-B, Mod-I, **x** and _x_ would all bypass that gate.
 */
const noEntryPoints = { addKeyboardShortcuts: () => ({}), addInputRules: () => [], addPasteRules: () => [] };
export const Bold = BaseBold.extend(noEntryPoints);
export const Italic = BaseItalic.extend(noEntryPoints);

/** Scale so the story's typical body size reads at about 14px in the panel. */
export function panelScale(story: Story, idx: SnapshotIndex): number {
  const sizes = story.paragraphs.map((p) => idx.paraStyles[p.styleId]?.attrs.pointSize ?? 10).sort((a, b) => a - b);
  const median = sizes[Math.floor(sizes.length / 2)] ?? 10;
  return 14 / median;
}

/** var(--ink) in styles/global.css: the colour the rest of the panel's chrome is written in. */
export const UI_INK = '#111111';

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/**
 * The rail paints text on its own fixed white panel, never on the artwork's background, so a fill
 * the document uses on colour (Headline-XL is Paper white) would be invisible here. Substitute the
 * UI ink below 3:1 against white, WCAG's minimum for large text, which is the size the rail shows
 * headings at; that is relative luminance 0.3, so mid grey and the brand blue still pass through.
 */
export function readableInk(hex: string): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return UI_INK;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join('') : m[1];
  const [r, g, b] = [0, 2, 4].map((i) => channel(parseInt(h.slice(i, i + 2), 16)));
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.3 ? UI_INK : hex;
}

export function editorStyles(idx: SnapshotIndex): Record<string, EditorStyleInfo> {
  const out: Record<string, EditorStyleInfo> = {};
  for (const s of Object.values(idx.paraStyles)) {
    out[s.id] = { name: s.name, fontId: s.attrs.fontId, pointSize: s.attrs.pointSize, leading: resolveLeading(s.attrs), fillHex: readableInk(s.attrs.fill?.hex ?? '#141414') };
  }
  return out;
}

/**
 * The editor is not created until the room has synced. y-prosemirror seeds the shared fragment from
 * ProseMirror's own document when the fragment is still empty, so mounting TipTap before the
 * server's state arrives writes an empty paragraph into the room and it survives the merge: the
 * story then reads as edited the moment it is opened, and the engine composes a phantom extra line.
 */
export function StoryEditor(props: StoryEditorProps) {
  const { room } = props;
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    setReady(false);
    room.synced.then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [room]);
  if (!ready) {
    return (
      <div className="editor-wrap">
        <div className="state"><div className="spinner" /><div className="muted small">Opening the shared document</div></div>
      </div>
    );
  }
  return <BoundEditor {...props} />;
}

function BoundEditor({ story, idx, room, threads, activeThreadId, editorRef, onChange, onSelection, onThreadClick }: StoryEditorProps) {
  const styles = useMemo(() => editorStyles(idx), [idx]);
  const scale = useMemo(() => panelScale(story, idx), [story, idx]);
  const resolved = useMemo(() => Object.fromEntries(threads.map((t) => [t.id, t.status === 'resolved'])), [threads]);
  // the same list DraftViewer hands materialise, so a face the draft declares missing is neither
  // marked here nor resolved there
  const fonts = useMemo(() => Object.fromEntries(Object.entries(idx.fonts).filter(([, f]) => isAvailableFace(f))), [idx.fonts]);
  const cbs = useRef({ onChange, onSelection });
  cbs.current = { onChange, onSelection };
  const [seeded, setSeeded] = useState(false);

  const editor = useEditor({
    extensions: [
      Document, Text, Bold, Italic, CharStyleMark, FaceMark,
      StyledParagraph.configure({ styles, scale }),
      CommentMark.configure({ resolved }),
      ParagraphIdentity,
      Collaboration.configure({ fragment: room.fragment }),
    ],
    editorProps: {
      attributes: { class: 'tiptap', spellcheck: 'true', 'aria-label': 'Story text' },
      handleClick(_view, _pos, event) {
        const el = (event.target as HTMLElement).closest?.('[data-thread]') as HTMLElement | null;
        if (el?.dataset.thread) { onThreadClick(el.dataset.thread); }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => { cbs.current.onChange(e.getJSON() as PMNode); },
    onSelectionUpdate: ({ editor: e }) => { cbs.current.onSelection(selectionInfo(e)); },
  }, [room, styles, scale]);

  useEffect(() => { editorRef.current = editor; return () => { editorRef.current = null; }; }, [editor, editorRef]);

  // The server seeds shared rooms; only a local (fixture) room is seeded here, from the snapshot story.
  useEffect(() => {
    if (!editor) return;
    if (room.local && room.fragment.length === 0) {
      const doc = storyToDoc(story, { paraStyles: idx.paraStyles, charStyles: idx.charStyles, fonts, threads });
      editor.commands.setContent(doc, { emitUpdate: false });
    }
    setSeeded(true);
    // threads are only used for the initial seed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, room, story, idx, fonts]);

  // Highlight the active thread's range.
  useEffect(() => {
    const root = editor?.view.dom;
    if (!root) return;
    root.querySelectorAll<HTMLElement>('[data-thread]').forEach((el) => el.classList.toggle('active', el.dataset.thread === activeThreadId));
  }, [editor, activeThreadId, seeded]);

  if (!editor) return <div className="state"><div className="spinner" /></div>;
  return (
    <div className="editor-wrap">
      {!seeded && <div className="muted small" style={{ marginBottom: 8 }}>Connecting to the shared document</div>}
      <EditorContent editor={editor} />
    </div>
  );
}
